import React from 'react';
import { css } from '@emotion/react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { InfoOutlineIcon } from '@applyboard/ui-icons';

import { IntentProps, useFullTheme } from '~/design';
import { useResponsive } from '~/utils';
import { allowedTooltipSymbols, checkIsTooltipAllowed } from '~/utils/tooltip';
import { IconWrapper, IconWrapperForButtonProps, TooltipCompatibleProps } from '~/internal';

import {
  InternalButtonProps,
  useButtonBaseStyle,
  useButtonEmphasisStyle,
} from '~/internal/InternalButton';
import { allowedBadgeSymbols } from '~/utils/badge';

export interface TooltipProps {
  /** The React component to use as the trigger for the tooltip. */
  children: React.ReactNode;

  /** If false, the tooltip will not show up. (default: true). */
  enabled?: boolean;

  /** The label text that pops out when the tooltip is open. */
  label: string;

  /** A callback function that is invoked when the tooltip is open or closed. */
  onOpenChange?: (value: boolean) => void;

  /** The placement of the popper relative to its reference. (default: top) */
  placement?:
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'bottom-end'
    | 'bottom'
    | 'bottom-start'
    | 'left-end'
    | 'left'
    | 'left-start';
}

interface TooltipScopeContext {
  disabled: boolean;
  label: TooltipProps['label'];
}

const TooltipContext = React.createContext<TooltipScopeContext | null>(null);

/**
 * A tooltip is a brief, informative message that appears when a user hovers over an element or gives keyboard focus to it.
 * - Do not use for information that is vital for the user to complete their task.
 * - Use on desktop only
 *
 * ## Accessibility ♿
 * Tooltip usage is allowed only on interactive elements such as Buttons and Links.
 * - Accessibility features:
 *   - Activate either on mouse hover event or keyboard focus. Also appears on screen press on touch screen enabled devices.
 *   - When trigger is focused through keyboard, tooltip disappears as Esc key is pressed.
 */
export function Tooltip({
  children,
  enabled = true,
  label,
  onOpenChange,
  placement = 'top',
}: TooltipProps) {
  const theme = useFullTheme();
  const responsive = useResponsive();

  if (children === null || children === undefined || !checkIsTooltipAllowed(children)) {
    throw new Error('Tooltip can only be used with interactive Crystal components');
  }

  const ref = React.useRef<HTMLButtonElement>(null);
  const tooltipTrigger = React.Children.only(children) as React.ReactElement;

  const handleTriggerClick = React.useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!tooltipTrigger.props.disabled) {
        tooltipTrigger.props.onClick?.();
      }
    },
    [tooltipTrigger.props],
  );

  React.useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('click', handleTriggerClick);
      return () => current.removeEventListener('click', handleTriggerClick);
    }
  }, [handleTriggerClick]);

  const [side, align = 'center'] = placement.split('-') as [
    'top' | 'left' | 'bottom' | 'right',
    'start' | 'center' | 'end',
  ];

  const contentStyles = css({
    ...responsive.css(theme.typography.bodyS),
    background: theme.colors.actionSecondary,
    borderRadius: theme.borderRadiuses.control,
    color: theme.colors.textOnAction,
    maxWidth: '240px',
    paddingBlock: theme.spacing[2],
    paddingInline: theme.spacing[4],
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    zIndex: theme.zIndex.tooltip,
  });

  if (!enabled) {
    return (
      <TooltipContext.Provider value={{ label, disabled: true }}>
        {children}
      </TooltipContext.Provider>
    );
  }

  return (
    <TooltipContext.Provider value={{ label, disabled: false }}>
      <RadixTooltip.Root onOpenChange={onOpenChange}>
        <RadixTooltip.Portal>
          <RadixTooltip.Content side={side} align={align} css={contentStyles}>
            {label}
            <RadixTooltip.Arrow
              width={16}
              height={8}
              css={{
                fill: theme.colors.actionSecondary,
              }}
            />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
        <RadixTooltip.Trigger ref={ref} asChild>
          {tooltipTrigger}
        </RadixTooltip.Trigger>
      </RadixTooltip.Root>
    </TooltipContext.Provider>
  );
}

export type TooltipIconProps = IntentProps &
  TooltipCompatibleProps &
  Pick<InternalButtonProps, 'emphasis' | 'variant'> & {
    /**
     * The display variation of the button.
     * @deprecated Use `emphasis` instead.
     */
    variant?: 'contained' | 'outlined' | 'highlighted' | 'transparent';
  };

Tooltip.Icon = React.forwardRef<HTMLButtonElement, TooltipIconProps>(function TooltipIcon(
  {
    'aria-describedby': ariaDescribedby,
    emphasis: _emphasis = 'transparent',
    intent = 'secondary',
    onBlur,
    onFocus,
    onPointerDown,
    onPointerLeave,
    onPointerMove,
    variant,
  },
  ref,
) {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error('`Tooltip.Icon` can only be rendered as a child of `Tooltip`');
  }

  const { label, disabled } = context;
  const emphasis = disabled ? 'transparent' : variant ?? _emphasis;

  const iconProps: IconWrapperForButtonProps = {
    contrast: emphasis === 'contained' ? 'inverted' : intent === 'secondary' ? 'high' : 'mid',
    disabled,
    intent: intent,
    selector: 'button',
  };

  const baseStyle = useButtonBaseStyle({
    borderRadius: 'icon',
    cursor: 'help',
    intent,
    width: 'hug',
  });
  const variantStyle = useButtonEmphasisStyle({ emphasis, intent });

  const tooltipIconStyle = css({
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'help',
  });

  return (
    <button
      aria-describedby={ariaDescribedby}
      aria-label={label}
      disabled={disabled}
      onBlur={onBlur}
      onFocus={onFocus}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onPointerMove={onPointerMove}
      ref={ref}
      css={[baseStyle, tooltipIconStyle, variantStyle]}
      type="button"
    >
      <IconWrapper {...iconProps}>
        <InfoOutlineIcon />
      </IconWrapper>
    </button>
  );
});

Object.assign(Tooltip, { [allowedBadgeSymbols.Tooltip]: true });
Object.assign(Tooltip.Icon, { [allowedTooltipSymbols.TooltipIcon]: true });
